import React, { useState, useEffect } from 'react';

import { Popover, List, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { isModelBusy } from '@libs/ModelAvailability';
import { getMoment, getStringDatesInRange } from '@libs/NwMoment';
import { showDateNoTime, parseDateTime } from '@libs/NwMoment';

import AuthorAndDepartmentForEventCard from '@components/CreatedBy/AuthorAndDepartmentForEventCard';
import EventTypeTag from '@components/EventDetails/EventTypeTag';
import MiniLoading from '@components/Gui/MiniLoading';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ProjectLine from '@components/ProjectLine/ProjectLine';
import { AgendaEventContent } from '@components/Schedule/Agenda/AgendaEventStyle';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const AvailabilityWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.75rem;

    color: ${(props) =>
        props.$isBusy
            ? props.theme.DangerColor
            : props.$isAvailable
              ? props.theme.SuccessColor
              : props.theme.TextColorMuted};
    text-transform: uppercase;
    cursor: ${(props) => (props.$isBusy ? 'pointer' : 'default')};
`;

const ModelAvailability = ({ startDate, endDate, modelId, eventId }) => {
    const [isFetchedAvailability, setFetchedAvailability] = useState(false);
    const [isFetchedEvents, setFetchedEvents] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [modelEvents, setModelEvents] = useState([]);

    useEffect(() => {
        handleFetchAvailability();
    }, [startDate, endDate]);

    const fetchEvents = async () => {
        setFetchedEvents(false);
        const start = getMoment(startDate).startOf('day');
        const end = getMoment(endDate).endOf('day');
        let newValues = {
            FDate: start,
            TDate: end,
            idmodels: [modelId],
            inclJobs: true,
            inclExtJobs: true,
            inclCastings: true,
            checkConflict: true,
            inclTravels: true,
            inclAccommodations: true,
            inclExternalJobs: true,
            inclFollowUps: true,
            inclMeetings: true,
            inclNotices: true,
            General: false,
            Warning: false,
        };
        Axios.post('/calendar/getevents', newValues)
            .then((modelEventsInfo) => {
                setModelEvents(modelEventsInfo.Events);
                setFetchedEvents(true);
            })
            .catch((error) => {
                //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
                console.error('error', error);
            });
    };

    const handleOpenChange = (open) => {
        if (open) {
            fetchEvents();
        }
    };

    const handleFetchAvailability = async () => {
        setFetchedAvailability(false);
        const dates = getStringDatesInRange(startDate, endDate);
        const newValues = {
            dates: dates,
            models: [modelId],
        };
        if (eventId) {
            newValues.eventid = eventId;
        }

        Axios.post('/calendar/checkmodelsavailability', newValues)
            .then((modelavailability) => {
                setIsBusy(isModelBusy(modelavailability, modelId));
                setFetchedAvailability(true);
            })
            .catch((error) => {
                //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
                console.error('error', error);
                notification.error({
                    message: 'Error fetching model availability',
                    description: error.message,
                });
                setIsBusy(null);
                setFetchedAvailability(true);
            });
    };

    const start = parseDateTime(startDate, 'notime');
    const end = parseDateTime(endDate, 'notime');

    return isFetchedAvailability ? (
        isBusy ? (
            <Popover
                placement="topLeft"
                content={
                    isFetchedEvents ? (
                        <List
                            size="small"
                            itemLayout="horizontal"
                            dataSource={modelEvents}
                            renderItem={(event) => (
                                <AgendaEventContent $cancel={event.Cancel}>
                                    {event.Project && <ProjectLine project={event.Project} ellipsis />}
                                    <EventTypeTag event={event} /> {event.Name}
                                    {event.Description && (
                                        <small>
                                            <NwEllipsis text={event.Description} rows={2} popover />
                                        </small>
                                    )}
                                    <small>
                                        {showDateNoTime(event.FromDate)} - {showDateNoTime(event.ToDate)}
                                    </small>
                                    <small>
                                        <AuthorAndDepartmentForEventCard object={event} />
                                    </small>
                                </AgendaEventContent>
                            )}
                        />
                    ) : (
                        <div>
                            <MiniLoading /> Loading model agenda...
                        </div>
                    )
                }
                title={start === end ? start : `${start} - ${end}`}
                trigger="click"
                onOpenChange={handleOpenChange}
            >
                <AvailabilityWrapper $isBusy>
                    <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                        <NwIcon icon={byPrefixAndName.fasl['calendar-exclamation']} />
                    </div>
                    <small>{`busy in this period (click to view events)`}</small>
                </AvailabilityWrapper>
            </Popover>
        ) : (
            <AvailabilityWrapper $isAvailable>
                <NwIcon icon={byPrefixAndName.fasl['calendar-check']} />
                <small>available for this period</small>
            </AvailabilityWrapper>
        )
    ) : (
        <AvailabilityWrapper>
            <MiniLoading />
            <small>checking availability...</small>
        </AvailabilityWrapper>
    );
};

export default ModelAvailability;
